import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  ExplanationData,
  saveCalculationWithNotes,
  getMotorcycleCalculation,
  // MotorcycleCalculationRequest,
  MotorcycleCalculationResponse,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import ActualCostCalculator, {
  ActualCostData,
} from "../../shared/ActualCostCalculator";

interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
  engine_capacity: string;
}

interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}

// מפתחות השדות לטולטיפס הסבר
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
  engine_capacity: "engine_capacity",
} as const;

// אופציות נפח מנוע
const ENGINE_OPTIONS = [
  { value: "", label: "בחר סוג מנוע", disabled: true },
  { value: "25", label: 'אינו עולה על 50 סמ"ק' },
  { value: "125", label: 'אינו עולה על 125 סמ"ק' },
  { value: "250", label: 'עולה על 125 סמ"ק אך אינו עולה על 250 סמ"ק' },
  { value: "500", label: 'עולה על 250 סמ"ק אך אינו עולה על 500 סמ"ק' },
  { value: "800", label: 'עולה על 500 סמ"ק אך אינו עולה על 800 סמ"ק' },
  { value: "1000", label: 'עולה על 800 סמ"ק' },
  { value: "hybrid", label: "היברידי" },
  { value: "0", label: "מנוע חשמלי" },
];

const MotorcycleCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
    engine_capacity: "",
  });

  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [calculationResult, setCalculationResult] =
    useState<MotorcycleCalculationResponse | null>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);
  const [userNotes, setUserNotes] = useState<string>("");
  const [actualCostData, setActualCostData] = useState<ActualCostData | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [safetyFeatures, setSafetyFeatures] = useState({
    abs: false,
    stability_control: false,
    integrated_braking: false,
    steering_damper: false,
    traction_control: false,
    three_wheeled: false,
  });

  // טעינת נתוני מחשבון
  useEffect(() => {
    loadCalculatorData();
  }, []);

  const loadCalculatorData = async () => {
    try {
      setIsLoading(true);
      const response = await calculatorService.getCalculatorData();

      if (response.res === "OK" && response.data) {
        setCurrencies(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
      } else {
        setError("שגיאה בטעינת נתוני המחשבון");
      }
    } catch (error) {
      setError("שגיאה בחיבור לשרת");
      console.error("Error loading calculator data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // טיפול בשינוי שדות
  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");

    // עדכון אוטומטי של שער חליפין
    if (field === "currency_type" && typeof value === "string") {
      const selectedCurrency = currencies.find(
        (curr) => curr.currency_code === value,
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          exchange_rate: Number(selectedCurrency.customs_rate).toFixed(4),
          insurance_currency_type: value,
          insurance_exchange_rate: Number(
            selectedCurrency.customs_rate,
          ).toFixed(4),
        }));
      }
    }
  };

  // טיפול בשינוי מספרים (למניעת אפסים מוזרים)
  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    if (value === "" || (!isNaN(Number(value)) && Number(value) >= 0)) {
      handleInputChange(field, value);
    }
  };

  // בדיקה אם המנוע חשמלי
  const isElectricEngine = () => {
    return formData.engine_capacity === "0";
  };

  // פונקציה לחישוב האופנוע
  const handleCalculate = async () => {
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }
    try {
      setIsLoading(true);
      setError("");

      // בדיקת שדות חובה
      const requiredFields = [
        "manufacturing_date",
        "vehicle_price_foreign",
        "currency_type",
        "exchange_rate",
        "engine_capacity",
        "shipping_cost_foreign",
        "local_expenses_ils",
      ];

      const missingFields = requiredFields.filter(
        (field) =>
          !formData[field as keyof FormData] ||
          formData[field as keyof FormData] === "",
      );

      if (missingFields.length > 0) {
        setError("אנא מלא את כל השדות החובה");
        return;
      }

      // בניית בקשה - למנוע חשמלי נשלח נפח 1 (כדי שלא יהיה 0)
      const engineCapacity = isElectricEngine()
        ? "electric"
        : formData.engine_capacity === "hybrid"
          ? "hybrid"
          : parseFloat(formData.engine_capacity);

      const calculationRequest: any = {
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) ||
          parseFloat(formData.exchange_rate),
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        has_origin_declaration: formData.has_origin_declaration,
        engine_capacity: engineCapacity,
      };

      const response = await getMotorcycleCalculation(calculationRequest);

      if (response.res === "OK" && response.data) {
        setCalculationResult(response.data);
        setCurrentStep(2);
      } else {
        setError(response.msg || "שגיאה בחישוב האופנוע");
      }
    } catch (error) {
      console.error("Calculation error:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setIsLoading(false);
    }
  };

  // פונקציית שמירה
  const handleSaveCalculation = async () => {
    if (!calculationResult) return;

    setIsSaving(true);
    try {
      const saveRequest: SaveCalculationRequest = {
        calculation_type: "motorcycle_vehicle",
        input_data: calculationResult.input_data,
        result_data: {
          ...calculationResult,
          safety_features: safetyFeatures,
          calculated_safety_discount: calculateSafetyDiscount(),
          final_total_with_discount:
            calculationResult.final_results.final_total -
            calculateSafetyDiscount(),
          actual_cost_data: actualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => navigate("/enduser/dashboard"), 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("Save error:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון אופנועים",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  // פונקציית וולידציית תאריך (מקסימום 2 שנים אחורה)
  const validateManufacturingDate = (date: string): boolean => {
    if (!date) return false;
    const manufacturingDate = new Date(date);
    const currentDate = new Date();
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(currentDate.getFullYear() - 2);
    return manufacturingDate >= twoYearsAgo && manufacturingDate <= currentDate;
  };
  const calculateSafetyDiscount = (): number => {
    if (!calculationResult) return 0;

    const taxRate =
      calculationResult.intermediate_calculations.engine_info.tax_rate_percent;
    const purchaseTax =
      calculationResult.intermediate_calculations.purchase_tax;

    let discount = 0;

    if (taxRate === 25 && safetyFeatures.abs) {
      discount += 500;
    }
    if (safetyFeatures.stability_control) {
      if (taxRate === 25) discount += 220;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }
    if (safetyFeatures.integrated_braking) {
      if (taxRate === 25) discount += 0;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }
    if (safetyFeatures.steering_damper) {
      if (taxRate === 25) discount += 220;
      else if (taxRate === 40) discount += 310;
      else if (taxRate === 50) discount += 400;
      else if (taxRate === 60) discount += 500;
      else if (taxRate === 70) discount += 600;
    }
    if (safetyFeatures.traction_control) {
      if (taxRate === 25) discount += 500;
      else if (taxRate === 40) discount += 650;
      else if (taxRate === 50) discount += 800;
      else if (taxRate === 60) discount += 970;
      else if (taxRate === 70) discount += 1130;
    }
    if (safetyFeatures.three_wheeled) {
      if (taxRate === 25) discount += 1300;
      else if (taxRate === 40) discount += 2000;
      else if (taxRate === 50) discount += 2300;
      else if (taxRate === 60) discount += 3000;
      else if (taxRate === 70) discount += 3000;
    }
    return Math.min(discount, purchaseTax);
  };
  // פונקציה לקבלת שם קבוצת המנוע
  const getSelectedEngineGroupName = (): string => {
    const selectedOption = ENGINE_OPTIONS.find(
      (option) => option.value === formData.engine_capacity,
    );
    return selectedOption ? selectedOption.label : "";
  };
  if (isLoading && currentStep === 1) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען נתוני מחשבון...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 text-6xl"></div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון אופנועים
          </h1>
          <p className="text-gray-600">חישוב מיסים עבור אופנועים ייבוא אישי</p>

          {/* Progress bar */}
          <div className="mt-6 mb-8">
            <div className="flex items-center justify-center space-x-4 rtl:space-x-reverse">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                1
              </div>
              <div
                className={`h-1 w-16 ${
                  currentStep >= 2 ? "bg-blue-600" : "bg-gray-200"
                }`}
              ></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= 2
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                2
              </div>
            </div>
            <div className="flex justify-center mt-2 space-x-8 rtl:space-x-reverse">
              <span
                className={`text-sm ${
                  currentStep >= 1
                    ? "text-blue-600 font-medium"
                    : "text-gray-500"
                }`}
              >
                נתוני קלט
              </span>
              <span
                className={`text-sm ${
                  currentStep >= 2
                    ? "text-blue-600 font-medium"
                    : "text-gray-500"
                }`}
              >
                תוצאות
              </span>
            </div>
          </div>
        </div>

        {/* {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-center">{error}</p>
          </div>
        )} */}

        {/* שלב 1: טופס הקלט */}
        {currentStep === 1 && (
          <MotionCard className="p-8">
            <h2 className="text-xl font-bold mb-6 text-center">
              שלב 1: פרטי האופנוע
            </h2>
            <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms-calculator"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    setError("");
                  }}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label
                  htmlFor="terms-calculator"
                  className="text-sm text-gray-700 flex-1"
                >
                  קראתי ואני מאשר/ת את{" "}
                  <Link
                    to="/terms-and-privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    תנאי השימוש ומדיניות הפרטיות
                  </Link>{" "}
                  *
                </label>
              </div>
            </div>
            <div className="space-y-6">
              {/* תאריך ייצור */}
              <div>
                <label
                  htmlFor="manufacturing_date"
                  className="flex items-center mb-2 font-medium"
                >
                  תאריך ייצור האופנוע{" "}
                  <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.manufacturing_date}
                    explanations={explanations}
                  />
                </label>
                <input
                  id="manufacturing_date"
                  type="date"
                  value={formData.manufacturing_date}
                  aria-required="true"
                  onChange={(e) =>
                    handleInputChange("manufacturing_date", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  max={new Date().toISOString().split("T")[0]}
                  min={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 2),
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                />
                {formData.manufacturing_date &&
                  !validateManufacturingDate(formData.manufacturing_date) && (
                    <p className="text-red-600 text-sm mt-1">
                      מחשבון זה מיועד לאופנועים עד שנתיים
                    </p>
                  )}
              </div>

              {/* סוג מנוע - Dropdown */}
              <div>
                <label
                  htmlFor="engine_capacity"
                  className="flex items-center mb-2 font-medium"
                >
                  סוג מנוע <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.engine_capacity}
                    explanations={explanations}
                  />
                </label>
                <select
                  id="engine_capacity"
                  value={formData.engine_capacity}
                  aria-required="true"
                  onChange={(e) =>
                    handleInputChange("engine_capacity", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {ENGINE_OPTIONS.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
                {formData.engine_capacity && (
                  <p className="text-sm text-gray-600 mt-1">
                    {isElectricEngine()
                      ? "⚡ מנוע חשמלי - ללא פליטות"
                      : `נבחר: ${getSelectedEngineGroupName()}`}
                  </p>
                )}
              </div>

              {/* ערך האופנוע */}
              <div>
                <label
                  htmlFor="vehicle_price_foreign"
                  className="flex items-center mb-2 font-medium"
                >
                  ערך האופנוע במטבע זר{" "}
                  <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.vehicle_price_foreign}
                    explanations={explanations}
                  />
                </label>
                <input
                  id="vehicle_price_foreign"
                  type="number"
                  value={formData.vehicle_price_foreign}
                  aria-required="true"
                  onChange={(e) =>
                    handleNumberInputChange(
                      "vehicle_price_foreign",
                      e.target.value,
                    )
                  }
                  placeholder="למשל: 15000"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                />
              </div>

              {/* סוג מטבע */}
              <div>
                <label
                  htmlFor="currency_type"
                  className="flex items-center mb-2 font-medium"
                >
                  סוג מטבע <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.currency_type}
                    explanations={explanations}
                  />
                </label>
                <select
                  id="currency_type"
                  value={formData.currency_type}
                  aria-required="true"
                  onChange={(e) =>
                    handleInputChange("currency_type", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">בחר מטבע</option>
                  {currencies.map((currency) => (
                    <option
                      key={currency.currency_code}
                      value={currency.currency_code}
                    >
                      {currency.currency_name_hebrew} ({currency.currency_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* שער חליפין */}
              <div>
                <label
                  htmlFor="exchange_rate"
                  className="flex items-center mb-2 font-medium"
                >
                  שער חליפין <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.exchange_rate}
                    explanations={explanations}
                  />
                </label>
                <input
                  id="exchange_rate"
                  type="number"
                  step="0.0001"
                  value={formData.exchange_rate}
                  aria-required="true"
                  onChange={(e) =>
                    handleNumberInputChange("exchange_rate", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  placeholder="0.0000"
                />
              </div>

              {/* עלות הובלה */}
              <div>
                <label
                  htmlFor="shipping_cost_foreign"
                  className="flex items-center mb-2 font-medium"
                >
                  עלות הובלה במטבע המקור{" "}
                  <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.shipping_cost_foreign}
                    explanations={explanations}
                  />
                </label>
                <input
                  id="shipping_cost_foreign"
                  type="number"
                  value={formData.shipping_cost_foreign}
                  aria-required="true"
                  onChange={(e) =>
                    handleNumberInputChange(
                      "shipping_cost_foreign",
                      e.target.value,
                    )
                  }
                  placeholder="0"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* עלות ביטוח */}
              <div>
                <label className="flex items-center mb-2 font-medium">
                  עלות ביטוח במטבע המקור
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.insurance_cost_foreign}
                    explanations={explanations}
                  />
                </label>
                <input
                  type="number"
                  value={formData.insurance_cost_foreign}
                  onChange={(e) =>
                    handleNumberInputChange(
                      "insurance_cost_foreign",
                      e.target.value,
                    )
                  }
                  placeholder="0"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* מטבע ביטוח */}
              <div>
                <label
                  htmlFor="insurance_currency_type"
                  className="flex items-center mb-2 font-medium"
                >
                  מטבע ביטוח
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.insurance_currency_type}
                    explanations={explanations}
                  />
                </label>
                <select
                  id="insurance_currency_type"
                  value={formData.insurance_currency_type}
                  onChange={(e) =>
                    handleInputChange("insurance_currency_type", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">בחר מטבע ביטוח</option>
                  {currencies.map((currency) => (
                    <option
                      key={currency.currency_code}
                      value={currency.currency_code}
                    >
                      {currency.currency_name_hebrew} ({currency.currency_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* שער ביטוח */}
              <div>
                <label className="flex items-center mb-2 font-medium">
                  שער חליפין ביטוח
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.insurance_exchange_rate}
                    explanations={explanations}
                  />
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.insurance_exchange_rate}
                  onChange={(e) =>
                    handleNumberInputChange(
                      "insurance_exchange_rate",
                      e.target.value,
                    )
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  placeholder="0.0000"
                />
              </div>

              {/* הוצאות מקומיות */}
              <div>
                <label
                  htmlFor="local_expenses_ils"
                  className="flex items-center mb-2 font-medium"
                >
                  הוצאות מקומיות בשקלים{" "}
                  <span className="text-red-500 mr-1">*</span>
                  <span className="sr-only">(שדה חובה)</span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.local_expenses_ils}
                    explanations={explanations}
                  />
                </label>
                <input
                  id="local_expenses_ils"
                  type="number"
                  value={formData.local_expenses_ils}
                  aria-required="true"
                  onChange={(e) =>
                    handleNumberInputChange(
                      "local_expenses_ils",
                      e.target.value,
                    )
                  }
                  placeholder="0"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* הצהרת מקור */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-center space-x-3 rtl:space-x-reverse">
                  <input
                    type="checkbox"
                    checked={formData.has_origin_declaration}
                    onChange={(e) =>
                      handleInputChange(
                        "has_origin_declaration",
                        e.target.checked,
                      )
                    }
                    className="w-5 h-5 text-blue-600"
                  />
                  <span className="font-medium">
                    יש לי הצהרת מקור בחשבון או תעודת מקור
                  </span>
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.has_origin_declaration}
                    explanations={explanations}
                  />
                </label>
                <p className="text-sm text-gray-600 mt-2 mr-8">
                  {formData.has_origin_declaration
                    ? "✅ פטור ממכס (0%)"
                    : "⚠️ חייב במכס 7%"}
                </p>
              </div>
            </div>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 mt-4">
                ❌ {error}
              </div>
            )}
            {/* כפתורי פעולה */}
            <div className="flex justify-between mt-8">
              <MotionButton
                onClick={() => navigate("/enduser/calculator")}
                variant="secondary"
                className="px-6 py-3"
              >
                ← חזור
              </MotionButton>

              <MotionButton
                onClick={handleCalculate}
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
              >
                {isLoading ? "מחשב..." : "חשב מיסים →"}
              </MotionButton>
            </div>
          </MotionCard>
        )}
        {/* שלב 2: תוצאות */}
        {currentStep === 2 && calculationResult && (
          <div className="space-y-6">
            {/* אזור התוצאה הניתן להדפסה (לא כולל אבזור בטיחותי, "דייק לי" וכרטיס השמירה) */}
            <div ref={printRef} className="space-y-6">
            <TaxYearNotice />
            {/* נתוני קלט שהוזנו */}
            <MotionCard className="p-6">
              <h3 className="font-bold text-lg mb-4 text-blue-800">
                📋 נתוני הקלט שהוזנו:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm bg-blue-50 p-4 rounded-lg">
                <div>
                  <strong>תאריך ייצור:</strong> {formData.manufacturing_date}
                </div>
                <div>
                  <strong>סוג מנוע:</strong> {getSelectedEngineGroupName()}
                </div>
                <div>
                  <strong>ערך האופנוע:</strong> {formData.vehicle_price_foreign}{" "}
                  {formData.currency_type}
                </div>
                <div>
                  <strong>שער חליפין:</strong> {formData.exchange_rate}
                </div>
                <div>
                  <strong>עלות הובלה:</strong>{" "}
                  {formData.shipping_cost_foreign || "0"}{" "}
                  {formData.currency_type}
                </div>
                <div>
                  <strong>עלות ביטוח:</strong>{" "}
                  {formData.insurance_cost_foreign || "0"}{" "}
                  {formData.insurance_currency_type || formData.currency_type}
                </div>
                <div>
                  <strong>הוצאות מקומיות:</strong>{" "}
                  {formData.local_expenses_ils || "0"} ש"ח
                </div>
                <div>
                  <strong>הצהרת מקור:</strong>{" "}
                  {formData.has_origin_declaration ? "✅ יש" : "❌ אין"}
                </div>
              </div>
            </MotionCard>

            {/* תוצאות החישוב - פשוט לפי בקשת הלקוח */}
            <MotionCard className="p-6">
              <h3 className="font-bold text-lg mb-6 text-center">
                תוצאות החישוב
              </h3>

              <div className="space-y-4 text-black">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">1. ערך לצרכי מכס:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.total_before_customs.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">2. סך המכס:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.customs_fee.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">3. שיעור המכס:</span>
                  <span className="font-bold">
                    {
                      calculationResult.intermediate_calculations
                        .customs_rate_percent
                    }
                    %
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">4. סך מס הקניה:</span>
                  <span className="font-bold">
                    {calculationResult.intermediate_calculations.purchase_tax.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">5. שיעור מס הקניה המלא:</span>
                  <span className="font-bold">
                    {
                      calculationResult.intermediate_calculations.engine_info
                        .tax_rate_percent
                    }
                    %
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">6. סך המע"מ:</span>
                  <span className="font-bold">
                    {calculationResult.final_results.vat.toLocaleString()} ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">7. שיעור המע"מ:</span>
                  <span className="font-bold">18%</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">8. סך המיסים:</span>
                  <span className="font-bold">
                    {(
                      calculationResult.intermediate_calculations.customs_fee +
                      calculationResult.intermediate_calculations.purchase_tax +
                      calculationResult.final_results.vat
                    ).toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="font-medium">9. שיעור המיסים:</span>
                  <span className="font-bold">
                    {(
                      ((calculationResult.intermediate_calculations
                        .customs_fee +
                        calculationResult.intermediate_calculations
                          .purchase_tax +
                        calculationResult.final_results.vat) /
                        calculationResult.intermediate_calculations
                          .total_before_customs) *
                      100
                    ).toFixed(1)}
                    %
                  </span>
                </div>

                <div className="flex justify-between items-center py-3 border-t-2 border-gray-800 bg-gray-50 px-4 rounded-lg">
                  <span className="font-bold text-lg">
                    10. עלות הרכב כולל מיסים:
                  </span>
                  <span className="font-bold text-xl text-green-700">
                    {calculationResult.final_results.final_total.toLocaleString()}{" "}
                    ש"ח
                  </span>
                </div>
              </div>
            </MotionCard>

            {/* הנחות אבזור בטיחותי - רק אם לא מנוע חשמלי */}
            {calculationResult && formData.engine_capacity !== "0" && (
              <MotionCard className="p-6 border-2 border-orange-200 bg-orange-50">
                <h3 className="font-bold text-lg mb-2 text-orange-800">
                  🛡️ ייתכן ומגיעה לך הנחת אבזור בטיחותי
                </h3>
                <p className="text-sm text-orange-700 mb-1">
                  חישוב רמת אבזור בטיחותי על פי נוהל 03/13 משרד התחבורה - ייתכן
                  ותהיה זכאי להחזר לאחר הטסט
                </p>
                <p className="text-sm text-orange-700 mb-3">
                  שאל את עמיל המכס שלך לפרטים נוספים
                </p>
                <ul className="text-xs text-orange-600 mb-4 list-disc list-inside space-y-1">
                  <li>
                    ייתכן ותהיה זכאי לאחר הטסט להחזר בגין אבזור בטיחותי (שאל את
                    עמיל המכס שלך)
                  </li>
                  <li>חישוב נלווה בלבד - לא משפיע על עלות הייבוא הסופית</li>
                </ul>
                <p className="text-sm text-orange-700 mb-4">
                  סמן את האבזור הבטיחותי הקיים באופנוע שלך לחישוב הנחה אפשרית:
                </p>

                <div className="space-y-3 mb-4">
                  {calculationResult.intermediate_calculations.engine_info
                    .tax_rate_percent === 25 && (
                    <label className="flex items-center space-x-3 rtl:space-x-reverse">
                      <input
                        type="checkbox"
                        checked={safetyFeatures.abs}
                        onChange={(e) =>
                          setSafetyFeatures((prev) => ({
                            ...prev,
                            abs: e.target.checked,
                          }))
                        }
                        className="w-4 h-4 text-orange-600"
                      />
                      <span className="text-sm">
                        מערכת ABS (רק עד 125 סמ"ק)
                      </span>
                    </label>
                  )}

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.stability_control}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          stability_control: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">מערכת בקרת יציבות</span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.integrated_braking}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          integrated_braking: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">מערכת בלימה משולבת</span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.steering_damper}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          steering_damper: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">משכך היגוי</span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.traction_control}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          traction_control: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">מערכת בקרת משיכה והחלקה</span>
                  </label>

                  <label className="flex items-center space-x-3 rtl:space-x-reverse">
                    <input
                      type="checkbox"
                      checked={safetyFeatures.three_wheeled}
                      onChange={(e) =>
                        setSafetyFeatures((prev) => ({
                          ...prev,
                          three_wheeled: e.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-orange-600"
                    />
                    <span className="text-sm">אופנוע תלת גלגלי</span>
                  </label>
                </div>

                {calculateSafetyDiscount() > 0 && (
                  <div className="bg-orange-100 p-4 rounded-lg border border-orange-300">
                    <p className="font-semibold text-orange-800 mb-2">
                      הנחה אפשרית: {calculateSafetyDiscount().toLocaleString()}{" "}
                      ש"ח
                    </p>
                    <p className="text-sm text-orange-700">
                      עלות סופית עם הנחה:{" "}
                      {(
                        calculationResult.final_results.final_total -
                        calculateSafetyDiscount()
                      ).toLocaleString()}{" "}
                      ש"ח
                    </p>
                  </div>
                )}

                {safetyFeatures.three_wheeled && (
                  <div className="mt-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <h4 className="font-semibold text-yellow-800 mb-2">
                      לעניין זה, "רכב תלת גלגלי" הוא רכב שמתקיימים לגביו כל
                      התנאים האלה :
                    </h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• יש לו שני גלגלים קדמיים ואחד מאחור</li>
                      <li>
                        • המרחק בין מרכז שני הגלגלים הקדמיים שלו לא יפחת מ-35
                        ס"מ ולא יעלה על 70 ס"מ;
                      </li>
                      <li>
                        • מערכת המתלים של הגלגלים הקדמיים מאפשרת אי-תלות בזמן
                        תנועה, בין תנועת שני הגלגלים בכל הצירים.
                      </li>
                    </ul>
                  </div>
                )}
              </MotionCard>
            )}
            {/* דייק לי את התוצאה */}
            <ActualCostCalculator
              vehicleCurrency={formData.currency_type}
              vehicleExchangeRate={formData.exchange_rate}
              vehiclePriceForeign={formData.vehicle_price_foreign}
              shippingCostForeign={formData.shipping_cost_foreign}
              insuranceCurrency={formData.insurance_currency_type}
              insuranceExchangeRate={formData.insurance_exchange_rate}
              insuranceCostForeign={formData.insurance_cost_foreign}
              localExpensesIls={formData.local_expenses_ils}
              totalTaxes={
                calculationResult
                  ? calculationResult.intermediate_calculations.customs_fee +
                    calculationResult.intermediate_calculations.purchase_tax +
                    calculationResult.final_results.vat -
                    calculateSafetyDiscount()
                  : 0
              }
              onDataChange={setActualCostData}
              explanations={explanations}
              initialData={actualCostData}
            />
            </div>
            {/* סוף אזור התוצאה הניתן להדפסה */}

            {/* שמירה והערות */}
            <MotionCard className="p-6 print-exclude">
              <h3 className="font-bold text-lg mb-4 text-gray-800">
                💾 שמירת החישוב
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  הערות נוספות (אופציונלי):
                </label>
                <textarea
                  value={userNotes}
                  onChange={(e) =>
                    e.target.value.length <= 1000 &&
                    setUserNotes(e.target.value)
                  }
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="הוסף הערות על החישוב, פרטים נוספים על האופנוע וכו'..."
                />
                <div className="text-sm text-gray-500 mt-1">
                  {userNotes.length}/1000 תווים
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex gap-4">
                  <MotionButton
                    onClick={() => setCurrentStep(1)}
                    variant="secondary"
                    className="px-6 py-3"
                  >
                    ← ערוך נתונים
                  </MotionButton>

                  <MotionButton
                    onClick={handlePrint}
                    variant="secondary"
                    className="px-6 py-3"
                  >
                    🖨️ הדפס
                  </MotionButton>
                </div>

                <MotionButton
                  onClick={handleSaveCalculation}
                  disabled={isSaving || isSaved}
                  className={`${
                    isSaved
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  } text-white px-6 py-3`}
                >
                  {isSaving && "שומר..."}
                  {isSaved && "נשמר ✓ חזרה לדף הבית"}
                  {!isSaving && !isSaved && "שמור וחזור לדף הבית"}
                </MotionButton>
              </div>
            </MotionCard>
          </div>
        )}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotorcycleCalculatorPage;
